import {
  IonIcon,
  IonItem,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { card } from 'ionicons/icons';
import moment from 'moment';

import Amex from '../../../assets/tarjetas/amex.svg';
import MasterCard from '../../../assets/tarjetas/mastercard.svg';
import Visa from '../../../assets/tarjetas/visa.svg';
import { useUser } from '../../../context/UserContext';
import { useEnter } from '../../../hooks/Utils/useEnter';
import { MaskType } from '../../../hooks/Utils/useMask';
import { PetForm_3 as PetFormType_3, PetForm_3_Defaults } from '../../../types/Pet/types';
import {
  cbuValidation,
  detectCardType,
  formatNumeroPago,
  getDefaultsByMetodo,
  isExpirationValid,
  isTipoCBU,
  luhnValidation,
  normalizeNumeroPago,
} from '../../../utils/pago';
import { capitalizeEveryWord } from '../../../utils/string';
import { validations } from '../../../utils/validations';
import Button from '../../UI/Button';
import MaskedFormField from '../../UI/MaskedFormField';
import Spinner from '../../UI/Spinner';
import './PetForm_3.scss';
import { PetForm_3Props } from './types';

const CARD_ICONS: Record<string, string> = {
  Visa,
  MasterCard,
  Amex,
};

const FIELD_CONFIG = {
  card: [
    {
      name: 'nombreTitular',
      label: 'Nombre completo',
      placeholder: 'Nombre como figura en la tarjeta',
      rules: {
        required: 'El nombre es requerido',
        pattern: validations.name,
      },
      transform: capitalizeEveryWord,
    },
    {
      name: 'numeroTarjeta',
      label: 'Número de tarjeta',
      placeholder: '0000 0000 0000 0000',
      rules: {
        required: 'El número de tarjeta es requerido',
        validate: (v: string) =>
          luhnValidation(normalizeNumeroPago(v)) || 'Número inválido',
      },
      maxLength: 19,
      maskType: 'cardMask' as MaskType,
      withIcon: true,
    },
    {
      name: 'vencimiento',
      label: 'Fecha de vencimiento',
      placeholder: 'MM/YY',
      rules: {
        required: 'La fecha es requerida',
        pattern: { value: /^(0[1-9]|1[0-2])\/\d{2}$/, message: 'Formato MM/YY' },
        validate: (v: string) => isExpirationValid(v) || 'Tarjeta vencida',
      },
      maxLength: 5,
      maskType: 'expirationMask' as MaskType,
    },
  ],
  cbu: [
    {
      name: 'cbu',
      label: 'Número de CBU',
      placeholder: 'Ingrese los 22 dígitos',
      maxLength: 22,
      rules: {
        required: 'El CBU es requerido',
        validate: (v: string) => cbuValidation(v) || 'CBU inválido',
      },
      maskType: 'cbuMask' as MaskType,
    },
  ],
} as const;

const PetForm_3: React.FC<PetForm_3Props> = ({
  datosPagoPreloaded,
  metodosPago,
  onSubmit: submitForm,
  isSubmitting,
  setIsCodVendedorAlertOpen,
  codVendedorAlertShownRef,
}) => {
  const { ref, handleEnter } = useEnter();
  const { user } = useUser();
  const userName = user ? `${user.nombre ?? ''} ${user.apellido ?? ''}`.trim() : '';

  const [metodoPago, setMetodoPago] = useState<'card' | 'cbu'>('card');
  const [cardType, setCardType] = useState('Unknown');
  const [opcionSeleccionada, setOpcionSeleccionada] = useState('');
  const metodoPagoRef = useRef(metodoPago);
  const metodoChangedByPreloadRef = useRef(false);

  // Mantener ref actualizado
  metodoPagoRef.current = metodoPago;

  const { control, handleSubmit, errors, watch, register, setValue, reset } =
    useForm<PetFormType_3>({ defaultValues: PetForm_3_Defaults });

  const numeroTarjeta = watch('numeroTarjeta');

  const opciones = useMemo(
    () => (metodoPago === 'card' ? metodosPago?.tarjeta : metodosPago?.cbu),
    [metodoPago, metodosPago],
  );

  const onSubmit = async (values: PetFormType_3) => {
    if (!codVendedorAlertShownRef.current) {
      codVendedorAlertShownRef.current = true;
      setIsCodVendedorAlertOpen(true);
      return;
    }

    await submitForm(values);
  };

  const getCardIcon = () => (
    <IonIcon
      slot="start"
      size="large"
      className="card-icon"
      {...(CARD_ICONS[cardType] ? { src: CARD_ICONS[cardType] } : { icon: card })}
    />
  );

  // Precargar datos guardados
  useEffect(() => {
    if (!datosPagoPreloaded) return;

    const { tipo, numero_pago, vencimiento, medioPago } = datosPagoPreloaded;
    const esCBU = isTipoCBU(tipo, medioPago?.verifica);
    const nuevoMetodo = esCBU ? 'cbu' : 'card';

    // Solo marcar si el método realmente va a cambiar
    if (metodoPagoRef.current !== nuevoMetodo) {
      metodoChangedByPreloadRef.current = true;
    }
    setMetodoPago(nuevoMetodo);

    const opcion = (esCBU ? metodosPago?.cbu : metodosPago?.tarjeta)?.find(
      (i) => i.tipo.toLowerCase() === tipo.toLowerCase(),
    );
    const opcionId = opcion ? String(opcion.id) : '';
    if (opcionId) {
      setOpcionSeleccionada(opcionId);
    }

    reset({
      ...PetForm_3_Defaults,
      tipo: opcionId,
      ...(esCBU
        ? { cbu: numero_pago || '' }
        : {
            nombreTitular: userName,
            numeroTarjeta: numero_pago ? formatNumeroPago(numero_pago) : '',
            vencimiento: vencimiento ? moment(vencimiento).format('MM/YY') : '',
          }),
    });

    if (!esCBU) {
      setCardType(detectCardType(numero_pago, tipo));
    }
  }, [datosPagoPreloaded, metodosPago, userName, setValue, reset]);

  // Detectar tipo de tarjeta por número
  useEffect(() => {
    if (numeroTarjeta) {
      setCardType(detectCardType(numeroTarjeta));
    }
  }, [numeroTarjeta]);

  // Reset al cambiar método (skip si fue por precarga)
  useEffect(() => {
    if (metodoChangedByPreloadRef.current) {
      metodoChangedByPreloadRef.current = false;
      return;
    }

    setOpcionSeleccionada('');
    setCardType('Unknown');

    reset({
      ...PetForm_3_Defaults,
      ...getDefaultsByMetodo(metodoPago),
    });
  }, [metodoPago, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyUp={handleEnter}>
      <div className="ion-margin-vertical">
        <IonSegment
          value={metodoPago}
          onIonChange={(e) => setMetodoPago(e.detail.value as 'card' | 'cbu')}
          mode='md'
        >
          <IonSegmentButton value="card">
            <IonLabel>Tarjeta de Crédito</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="cbu">
            <IonLabel>CBU</IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </div>

      <input type="hidden" {...(register('tipo') as any)} />

      <IonItem>
        <IonLabel position="stacked">
          {metodoPago === 'card' ? 'Seleccione tarjeta' : 'Seleccione cuenta bancaria'}
        </IonLabel>
        <IonSelect
          value={opcionSeleccionada}
          placeholder="Seleccione una opción"
          onIonChange={(e) => {
            setOpcionSeleccionada(e.detail.value);
            setValue('tipo', e.detail.value);
          }}
          okText="Confirmar"
          cancelText="Cancelar"
        >
          {opciones?.map((item) => (
            <IonSelectOption key={item.id} value={String(item.id)}>
              {capitalizeEveryWord(item.tipo)}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>

      {FIELD_CONFIG[metodoPago].map(
        ({
          name,
          label,
          placeholder,
          maxLength,
          rules,
          maskType,
          withIcon,
          transform,
        }) => (
          <MaskedFormField
            key={name}
            name={name as keyof PetFormType_3}
            label={label}
            placeholder={placeholder}
            maxLength={maxLength}
            control={control}
            errors={errors}
            rules={rules}
            transform={transform}
            maskType={maskType}
            withIcon={withIcon}
            opcionSeleccionada={opcionSeleccionada}
            cardType={cardType}
            getCardIcon={getCardIcon}
          />
        ),
      )}

      <Button ref={ref} type="submit" disabled={!opcionSeleccionada || isSubmitting}>
        {isSubmitting ? <Spinner color="light" /> : 'Finalizar'}
      </Button>
    </form>
  );
};

export default React.memo(PetForm_3);
